// Client for the KnowYourChord API (knowyourchord-api): ask for a video's chords, then poll the
// job until it's done. The server answers at once when the video was analysed before.
import type { AnalysisResult } from '@/core/analysis/types';

export type JobStage = 'queued' | 'downloading' | 'analysing';

/** Which recognizer the server uses: its copy of this app's engine, or the BTC model. */
export type ChordModel = 'dsp' | 'btc';

export interface VideoChords {
  title: string | null;
  result: AnalysisResult;
}

interface JobResponse {
  jobId?: string;
  status?: JobStage | 'done' | 'failed';
  position?: number;
  analysis?: { title: string | null; result: AnalysisResult };
  error?: { code: string; message: string };
}

export class ChordsApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ChordsApiError';
  }
}

const POLL_MS = 1000;
const GIVE_UP_MS = 5 * 60_000;

function done(body: JobResponse): VideoChords | null {
  if (body.status !== 'done' || !body.analysis) return null;
  // Checked at run time: an updated server may send a newer format.
  if ((body.analysis.result.schemaVersion as number) !== 1)
    throw new ChordsApiError('unsupported', 'The server sent chords this version can’t read.');
  return { title: body.analysis.title, result: body.analysis.result };
}

function failure(body: JobResponse, status: number): ChordsApiError {
  if (body.error) return new ChordsApiError(body.error.code, body.error.message);
  return new ChordsApiError('internal', `The chords server answered ${status}.`);
}

async function call(
  url: string,
  init: RequestInit,
): Promise<{ status: number; body: JobResponse }> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    if (init.signal?.aborted) throw e;
    throw new ChordsApiError('offline', 'Couldn’t reach the chords server.');
  }
  const body = (await res.json().catch(() => ({}))) as JobResponse;
  return { status: res.status, body };
}

const wait = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason as Error);
      },
      { once: true },
    );
  });

/** The chords for a video, waiting for the server to analyse it if needed. */
export async function fetchVideoChords(
  api: string,
  videoId: string,
  {
    signal,
    onStage,
    model = 'dsp',
  }: {
    signal: AbortSignal;
    onStage: (stage: JobStage, position: number) => void;
    model?: ChordModel;
  },
): Promise<VideoChords> {
  let { status, body } = await call(`${api}/v1/jobs`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url: `https://www.youtube.com/watch?v=${videoId}`, model }),
    signal,
  });
  const started = Date.now();
  for (;;) {
    const result = done(body);
    if (result) return result;
    if (status >= 400 || body.status === 'failed') throw failure(body, status);
    if (!body.jobId) throw failure(body, status);
    onStage(body.status === 'done' || !body.status ? 'queued' : body.status, body.position ?? 0);
    if (Date.now() - started > GIVE_UP_MS)
      throw new ChordsApiError('timeout', 'The chords server is taking too long. Try again later.');
    await wait(POLL_MS, signal);
    const jobId = body.jobId;
    ({ status, body } = await call(`${api}/v1/jobs/${jobId}`, { signal }));
    body.jobId ??= jobId;
  }
}
